import {
	CreateDrawerFormProps,
	DateField,
	DrawerBody,
	DrawerFooter,
	DrawerFooterActions,
	DrawerHeader,
	DrawerHeading,
	FormDrawer,
	FormDrawerContentProps,
	FormFetchDrawer,
	SubmitButton,
	TextField,
	UpdateDrawerFormProps,
} from "@dgs/core";
import { EmployeeOptionDto, VacationShowDto } from "apiGen";
import { useFormikContext } from "formik";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { EmployeeSingleSelectField } from "~root/employees/EmployeeSelectField";
import { HalfDayField } from "~root/vacations/HalfDayField";
import { vacationOwnService } from "~shared/api/vacation/vacation_service";

export type IVacationFormState = Omit<VacationShowDto, "id" | "employee" | "standIn" | "vacationDayCount"> & {
	standIn?: EmployeeOptionDto | null;
};

const useValidate = () => {
	const { t } = useTranslation();

	return (values: IVacationFormState) => {
		const errors: { [K in keyof IVacationFormState]?: any } = {};

		if (!values.start) {
			errors.start = t("Field is required");
		}
		if (!values.end) {
			errors.end = t("Field is required");
		}

		return errors;
	};
};

interface OwnVacationDrawerContentProps extends FormDrawerContentProps {
	footer?: ReactNode;
}

const OwnVacationDrawerContent: FC<OwnVacationDrawerContentProps> = ({ title, onClose, footer }) => {
	const { t } = useTranslation();
	const { values } = useFormikContext<IVacationFormState>();
	const updateDisabled = values.status === "approved";

	return (
		<>
			<DrawerHeader onClose={onClose}>
				<DrawerHeading>{title}</DrawerHeading>
			</DrawerHeader>
			<DrawerBody>
				<DateField name="start" label={t("Start")} required disabled={updateDisabled} />
				<DateField name="end" label={t("End")} required disabled={updateDisabled} />
				<HalfDayField name="halfDay" label={t("Half day")} />
				<TextField name="remark" label={t("Remark")} />
				<EmployeeSingleSelectField name="standIn" label={t("Stand in")} />
			</DrawerBody>
			<DrawerFooter>
				<DrawerFooterActions>{footer}</DrawerFooterActions>
				<SubmitButton>{t("Save")}</SubmitButton>
			</DrawerFooter>
		</>
	);
};

export const OwnVacationCreateDrawer: FC<CreateDrawerFormProps<IVacationFormState>> = (drawerProps) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const initialValues: IVacationFormState = {
		start: new Date().toISOString(),
		end: new Date().toISOString(),
		remark: "",
		declineReason: null,
		status: "pending",
		halfDay: false,
		standIn: null,
	};

	return (
		<FormDrawer
			{...drawerProps}
			validate={validate}
			title={t("Add vacation")}
			initialValues={initialValues}
			renderForm={(props) => <OwnVacationDrawerContent {...props} />}
		/>
	);
};

interface OwnVacationUpdateDrawer extends UpdateDrawerFormProps<IVacationFormState> {
	footer?: ReactNode;
}

export const OwnVacationUpdateDrawer: FC<OwnVacationUpdateDrawer> = ({ footer, ...drawerProps }) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<FormFetchDrawer
			{...drawerProps}
			validate={validate}
			title={t("Edit vacation")}
			queryFn={() => vacationOwnService.show(drawerProps.id).then((x) => x.data.data)}
			queryKey={vacationOwnService.showKey(drawerProps.id)}
			renderSuccess={(props) => <OwnVacationDrawerContent {...props} footer={footer} />}
		/>
	);
};
